import { _ } from "../../../../core/polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import EmailContentEditorWidget from "../../../../core/widgets/EmailContentEditorWidget.js";
import notifier from "../../../../core/widgets/notifier/notifier.js";
import NotifyUsersWidget from "../../../shared/NotifyUsersWidget.js";
import SwitchButton from "../../../../core/widgets/SwitchButton/SwitchButton.js";
import ToggableWidget from "../../../../core/widgets/ToggableWidget/ToggableWidget.js";

/**
 * `EmailNotificationWidget` selects recipients of an email and edits the email content.
 *
 * @param {EmailContent} spec.emailContent - Container of the email subject
 * and body to be edited.
 * @param {company} spec.currentCompany - Currently viewed company.
 * @param {user[]} spec.companyUsers - List of all users related directly to
 * the company to choose from.
 * @param {user[]} spec.agencyUsers - List of all users related to the agency
 * for the company to choose from.
 * @param {function} [spec.submitCallback] - Callback to send the notification.
 */
export default class EmailNotificationWidget extends Widget {
	constructor({
		emailContent,
		currentCompany,
		companyUsers,
		agencyUsers,
		submitCallback = () => {},
	}) {
		super(...arguments);

		this._emailContent = emailContent;
		this._currentUser = emailContent.getSender();
		this._currentCompany = currentCompany;
		this._submitCallback = submitCallback;

		this._companyUsers = companyUsers.filter(
			(user) => user !== this._currentUser,
		);

		this._agencyUsers = agencyUsers.filter(
			(user) => user !== this._currentUser,
		);

		this._notifyUsersWidget = new NotifyUsersWidget({
			currentUser: this._currentUser,
			currentCompany: this._currentCompany,
			companyUsers: this._companyUsers,
			agencyUsers: this._agencyUsers,
		});

		this._emailEditorSwitch = new SwitchButton({
			state: false,
			dataTestName: "customize-notification",
		});

		this._emailEditorSwitch.stateChanged.register((state) =>
			this._emailContentEditorWidget.setVisibility(state),
		);

		this._emailEditor = new EmailContentEditorWidget({
			emailContent: this._emailContent,
		});

		this._emailContentEditorWidget = new ToggableWidget({
			visible: false,
			content: this._emailEditor,
			cssClass: "email-editor",
		});
	}

	renderContentOn(html) {
		html.div(
			{ klass: "email-notification" },
			this._notifyUsersWidget,
			this._renderSwitchOn.bind(this),
			this._emailContentEditorWidget,
		);
	}

	submit() {
		if (this._emailContentEditorWidget.isVisible()) {
			this._emailEditor.submit();
		}

		const recipients = this._notifyUsersWidget.getSelectedUsers();
		this._emailContent.setRecipients(recipients);

		this._validate({
			onSuccess: () => this._submitCallback(),
			onError: (errors) => this._notifyErrors(errors),
		});
	}

	_renderSwitchOn(html) {
		html.div(
			{ klass: "modal-options" },
			html.div(
				{ klass: "modal-option" },
				html.h3(_("Customize notification text")),
				this._emailEditorSwitch,
			),
		);
	}

	_validate({ onSuccess, onError }) {
		const errors = this._emailContent.validation().getErrors();
		errors.length ? onError(errors) : onSuccess();
	}

	_notifyErrors(errors) {
		errors.forEach((error) => notifier.error(error.getDescription()));
	}
}
